// The live edge (UI-C3, T-302). The canvas is pinned to the newest line; ONLY a person's input un-pins it.
// Growth of the content, a web-font swap (font-display: swap) and our own scrolling also fire "scroll" or
// move the layout; none of them is intent, so none of them may park the room off-live.
//
// Intent is armed by input that can move the view up (wheel up, a touch drag, a pointer press, a key
// that scrolls up) and disarmed by our own scroll to the bottom, or by a tap that ended without moving.

export interface ScrollBox {
  scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
  addEventListener(type: string, fn: (ev: Event) => void, opts?: AddEventListenerOptions): void;
  removeEventListener(type: string, fn: (ev: Event) => void): void;
}

const UP_KEYS = new Set(["ArrowUp", "PageUp", "Home"]);
/** Within this many px of the end counts as "at the bottom" (sub-pixel rounding, not a tuning value). */
const EDGE_PX = 2;

export class ScrollPin {
  pinned = true;
  private intent = false;
  private movedDuringGesture = false;
  private readonly off: Array<() => void> = [];

  constructor(
    private readonly box: ScrollBox,
    private readonly onChange: (pinned: boolean) => void,
  ) {}

  attach(): () => void {
    const on = (type: string, fn: (ev: Event) => void) => {
      this.box.addEventListener(type, fn, { passive: true });
      this.off.push(() => this.box.removeEventListener(type, fn));
    };
    on("wheel", (ev) => {
      if ((ev as WheelEvent).deltaY < 0) this.intent = true;
    });
    on("touchstart", () => this.beginGesture());
    on("touchmove", () => (this.intent = true));
    on("touchend", () => this.endGesture());
    on("pointerdown", () => this.beginGesture());
    on("pointerup", () => this.endGesture());
    on("keydown", (ev) => {
      const k = ev as KeyboardEvent;
      if (UP_KEYS.has(k.key) || (k.key === " " && k.shiftKey)) this.intent = true;
    });
    on("scroll", () => this.onScroll());
    return () => this.detach();
  }

  detach(): void {
    this.off.splice(0).forEach((f) => f());
  }

  /** Content grew or re-laid out (new bubble, font swap, resize): keep the live edge if pinned. */
  onGrowth(): void {
    if (this.pinned) this.stick();
  }

  /** The jump pill (UI-C11): back to live. */
  jumpToLive(): void {
    this.set(true);
    this.stick();
  }

  private beginGesture(): void {
    this.intent = true;
    this.movedDuringGesture = false;
  }

  private endGesture(): void {
    if (!this.movedDuringGesture) this.intent = false; // a tap is not a scroll
  }

  private atBottom(): boolean {
    const b = this.box;
    return b.scrollHeight - b.scrollTop - b.clientHeight <= EDGE_PX;
  }

  private onScroll(): void {
    this.movedDuringGesture = true;
    if (!this.intent) {
      // not a person: our own scroll, growth, a font swap, scroll anchoring. Hold the live edge.
      if (this.pinned && !this.atBottom()) this.stick();
      return;
    }
    this.set(this.atBottom());
  }

  private stick(): void {
    this.intent = false;
    const b = this.box;
    b.scrollTop = b.scrollHeight;
  }

  private set(pinned: boolean): void {
    if (pinned === this.pinned) return;
    this.pinned = pinned;
    this.onChange(pinned);
  }
}
